import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import Modal from './Modal';
import SpecialistModal from './SpecialistModal';

function Users() {
  const { user: currentUser } = useAuth();
  const isSuperAdmin = !!currentUser?.is_super_admin;

  const [users, setUsers] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [specialistsByUserId, setSpecialistsByUserId] = useState({});
  const [specialistTarget, setSpecialistTarget] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [totalPages, setTotalPages] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    full_name: '',
    phone: '',
    is_active: true,
    is_admin: false,
    organization_id: '',
  });

  const fetchUsers = async (pageNum = 1) => {
    setLoading(true);
    try {
      const response = await api.getUsers(pageNum, 20, search);
      setUsers(response.items || []);
      setTotalPages(response.total_pages || 0);
      setPage(pageNum);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchOrganizations = async () => {
    try {
      const response = await api.getOrganizations();
      setOrganizations(response || []);
    } catch (err) {
      setError(err.message);
    }
  };

  // Lấy toàn bộ hồ sơ chuyên viên trong phạm vi của người đăng nhập để đánh dấu lên bảng user
  const fetchSpecialists = async () => {
    try {
      const pageSize = 100;
      const first = await api.getSpecialists({ page: 1, pageSize });
      let items = first.items || [];
      const totalPages = Math.min(first.total_pages || 1, 10);

      for (let p = 2; p <= totalPages; p += 1) {
        const next = await api.getSpecialists({ page: p, pageSize });
        items = items.concat(next.items || []);
      }

      const byUserId = {};
      items.forEach((specialist) => {
        byUserId[specialist.user_id] = specialist;
      });
      setSpecialistsByUserId(byUserId);
    } catch (err) {
      setError(err.message);
    }
  };

  useEffect(() => {
    fetchUsers(1);
  }, [search]);

  useEffect(() => {
    if (isSuperAdmin) {
      fetchOrganizations();
    }
  }, [isSuperAdmin]);

  useEffect(() => {
    fetchSpecialists();
  }, []);

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      username: user.username,
      email: user.email,
      full_name: user.full_name || '',
      phone: user.phone || '',
      is_active: user.is_active,
      is_admin: user.is_admin,
      organization_id: user.organization_id || '',
    });
    setShowForm(true);
  };

  const handleDelete = async (userId) => {
    if (window.confirm('Are you sure?')) {
      try {
        await api.deleteUser(userId);
        fetchUsers(page);
      } catch (err) {
        setError(err.message);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        await api.updateUser(editingUser.id, formData);
      } else {
        await api.createUser(formData);
      }
      setShowForm(false);
      setEditingUser(null);
      fetchUsers(page);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  return (
    <div className="content-section">
      <h1>Users</h1>

      {error && <div className="error-message">{error}</div>}

      <div className="toolbar">
        <input
          type="text"
          placeholder="Search users..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="search-input"
        />
        <button className="btn btn-primary" onClick={() => {
          setEditingUser(null);
          setFormData({
            username: '',
            email: '',
            password: '',
            full_name: '',
            phone: '',
            is_active: true,
            is_admin: false,
            organization_id: '',
          });
          setShowForm(true);
        }}>
          Add User
        </button>
      </div>

      {showForm && (
        <Modal
          title={editingUser ? 'Edit User' : 'Create User'}
          onClose={() => setShowForm(false)}
        >
          <form onSubmit={handleSubmit}>
            {isSuperAdmin && (
              <div className="form-group">
                <label>Organization</label>
                <select
                  name="organization_id"
                  value={formData.organization_id}
                  onChange={handleInputChange}
                >
                  <option value="">-- None --</option>
                  {organizations.map((org) => (
                    <option key={org.id} value={org.id}>
                      {org.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="form-group">
              <label>Username *</label>
              <input
                type="text"
                name="username"
                value={formData.username}
                onChange={handleInputChange}
                required
                disabled={!!editingUser}
              />
            </div>

            <div className="form-group">
              <label>Email *</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                disabled={!!editingUser}
              />
            </div>

            {!editingUser && (
              <div className="form-group">
                <label>Password *</label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                />
              </div>
            )}

            <div className="form-group">
              <label>Full Name</label>
              <input
                type="text"
                name="full_name"
                value={formData.full_name}
                onChange={handleInputChange}
              />
            </div>

            <div className="form-group">
              <label>Phone</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
              />
            </div>

            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleInputChange}
                />
                Active
              </label>
            </div>

            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  name="is_admin"
                  checked={formData.is_admin}
                  onChange={handleInputChange}
                />
                Admin
              </label>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary">Save</button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowForm(false)}
              >
                Cancel
              </button>
            </div>
          </form>
        </Modal>
      )}

      {specialistTarget && (
        <SpecialistModal
          user={specialistTarget.user}
          specialist={specialistTarget.specialist}
          isSuperAdmin={isSuperAdmin}
          onClose={() => setSpecialistTarget(null)}
          onSaved={fetchSpecialists}
        />
      )}

      {loading ? (
        <p>Loading...</p>
      ) : (
        <>
          <table className="table">
            <thead>
              <tr>
                <th>Username</th>
                <th>Email</th>
                <th>Full Name</th>
                <th>Phone</th>
                {isSuperAdmin && <th>Organization</th>}
                <th>Chuyên viên</th>
                <th>Admin</th>
                <th>Active</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => {
                const specialist = specialistsByUserId[user.id];
                return (
                <tr key={user.id}>
                  <td>{user.username}</td>
                  <td>{user.email}</td>
                  <td>{user.full_name || '-'}</td>
                  <td>{user.phone || '-'}</td>
                  {isSuperAdmin && <td>{user.organization_name || '-'}</td>}
                  <td>
                    {specialist ? (
                      <span
                        className={`specialist-badge${specialist.is_active ? '' : ' is-revoked'}`}
                        title={specialist.title || ''}
                      >
                        {specialist.is_active ? 'Chuyên viên' : 'Đã thu hồi'}
                      </span>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td>{user.is_admin ? '✓' : '-'}</td>
                  <td>{user.is_active ? '✓' : '-'}</td>
                  <td>
                    <button
                      className="btn btn-small"
                      onClick={() => handleEdit(user)}
                    >
                      Edit
                    </button>
                    {(isSuperAdmin || specialist) && (
                      <button
                        className="btn btn-small"
                        onClick={() => setSpecialistTarget({ user, specialist: specialist || null })}
                      >
                        {!specialist ? 'Cấp chuyên viên' : isSuperAdmin ? 'Hồ sơ chuyên viên' : 'Xem chuyên viên'}
                      </button>
                    )}
                    {/* Tạm thời ẩn nút Delete, bật lại khi cần
                    <button
                      className="btn btn-small btn-danger"
                      onClick={() => handleDelete(user.id)}
                    >
                      Delete
                    </button>
                    */}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>

          <div className="pagination">
            <button
              onClick={() => fetchUsers(page - 1)}
              disabled={page <= 1}
              className="btn btn-small"
            >
              Previous
            </button>
            <span>Page {page} of {totalPages}</span>
            <button
              onClick={() => fetchUsers(page + 1)}
              disabled={page >= totalPages}
              className="btn btn-small"
            >
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default Users;
